import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
} from "@mui/material";
import { ChevronLeft, ChevronRight, Add } from "@mui/icons-material";
import sh from "bundles/common/utils/sh";
import CreateTimeEntryModal from "./CreateTimeEntryModal";

const FONT = "Segoe UI, sans-serif, system-ui";
const TIME_ENTRY_CHANGED_EVENT = "time-entry:changed";
const TIME_PROJECT_CHANGED_EVENT = "time-project:changed";
const HOURS = Array.from({ length: 12 }, (_, i) => 8 + i);
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function pad(n) { return String(n).padStart(2, "0"); }

function startOfWeek(date) {
  const d = new Date(date);
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
}

function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function formatHM(secs) {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (h === 0 && m === 0) return "0m";
  if (h === 0) return `${m}m`;
  return `${h}h ${pad(m)}m`;
}

function assignLanes(entries) {
  if (!entries.length) return [];
  const sorted = [...entries].sort((a, b) => a.startMs - b.startMs || a.endMs - b.endMs);
  const out = [];
  let cluster = [];
  let clusterEnd = -Infinity;

  const flush = (c) => {
    const lanesEnd = [];
    const placements = [];
    for (const e of c) {
      let lane = lanesEnd.findIndex((endMs) => endMs <= e.startMs);
      if (lane === -1) {
        lane = lanesEnd.length;
        lanesEnd.push(e.endMs);
      } else {
        lanesEnd[lane] = e.endMs;
      }
      placements.push({ ...e, lane });
    }
    const laneCount = lanesEnd.length;
    placements.forEach((p) => out.push({ ...p, laneCount }));
  };

  for (const e of sorted) {
    if (cluster.length === 0 || e.startMs < clusterEnd) {
      cluster.push(e);
      clusterEnd = Math.max(clusterEnd, e.endMs);
    } else {
      flush(cluster);
      cluster = [e];
      clusterEnd = e.endMs;
    }
  }
  if (cluster.length) flush(cluster);
  return out;
}

export default function CalendarView({ refreshKey, compact = false }) {
  const authenticity_token = useSelector((s) => s.authenticity_token);

  const [entries, setEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [dialog, setDialog] = useState({ open: false, date: null, hour: null, entry: null });

  const weekEnd = useMemo(() => {
    const d = new Date(weekStart); d.setDate(d.getDate() + 7);
    return d;
  }, [weekStart]);

  const fetchEntries = useCallback(() => {
    sh.get("/internal/time_entries?caller=calendar", {
      params: {
        from: weekStart.toISOString(),
        to: weekEnd.toISOString(),
      },
    }).then((res) => setEntries(Array.isArray(res.data) ? res.data : []));
  }, [weekStart, weekEnd]);

  useEffect(() => { fetchEntries(); }, [fetchEntries, refreshKey]);

  const fetchProjects = useCallback(() => {
    sh.get("/internal/time_projects?caller=calendar").then((res) => setProjects(res.data || []));
  }, []);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  useEffect(() => {
    const handler = () => fetchProjects();
    window.addEventListener(TIME_PROJECT_CHANGED_EVENT, handler);
    return () => window.removeEventListener(TIME_PROJECT_CHANGED_EVENT, handler);
  }, [fetchProjects]);

  useEffect(() => {
    const handler = () => fetchEntries();
    window.addEventListener(TIME_ENTRY_CHANGED_EVENT, handler);
    return () => window.removeEventListener(TIME_ENTRY_CHANGED_EVENT, handler);
  }, [fetchEntries]);

  const projectColors = useMemo(() => {
    const map = {};
    entries.forEach((e) => { if (e.project?.id && e.project.color) map[e.project.id] = e.project.color; });
    projects.forEach((p) => { if (p.id && p.color) map[p.id] = p.color; });
    return map;
  }, [entries, projects]);

  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart); d.setDate(d.getDate() + i); d.setHours(0, 0, 0, 0); return d;
  }), [weekStart]);

  const entriesByDay = useMemo(() => weekDays.map((day) => {
    const dayEntries = entries
      .filter((e) => e.started_at && e.ended_at)
      .map((e) => ({
        ...e,
        startMs: new Date(e.started_at).getTime(),
        endMs:   new Date(e.ended_at).getTime(),
      }))
      .filter((e) => sameDay(new Date(e.startMs), day));
    return { day, items: assignLanes(dayEntries) };
  }), [entries, weekDays]);

  const rowH = compact ? 36 : 48;

  const navWeek = (delta) => {
    const d = new Date(weekStart); d.setDate(d.getDate() + delta * 7);
    setWeekStart(d);
  };

  const openSlot = (day, hour) => setDialog({ open: true, date: day, hour, entry: null });
  const openEntry = (entry) => setDialog({ open: true, date: null, hour: null, entry });
  const closeDialog = () => setDialog({ open: false, date: null, hour: null, entry: null });

  const handleSaved = () => {
    fetchEntries();
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(TIME_ENTRY_CHANGED_EVENT));
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 4,
        borderRadius: "20px",
        backgroundColor: "#ffffff",
        boxShadow: "0 4px 24px hsla(150, 30%, 12%, 0.06), 0 1px 3px hsla(150, 30%, 12%, 0.04)",
        fontFamily: "Segoe UI Regular, sans-serif, system-ui",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 1.5 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui", fontSize: 22, color: "hsl(150 30% 12%)", letterSpacing: -0.2 }} noWrap>
            Week of {weekStart.toLocaleDateString(undefined, { month: "long", day: "numeric" })}
          </Typography>
          <Typography sx={{ fontFamily: "Segoe UI Regular, sans-serif, system-ui", fontSize: 13, color: "hsl(150 10% 46%)", mt: 0.25 }}>
            Click an empty slot to log time at that hour
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
          <Button
            onClick={() => setDialog({ open: true, date: new Date(), hour: new Date().getHours(), entry: null })}
            startIcon={<Add sx={{ fontSize: 16 }} />}
            sx={{
              height: 32, px: 1.5, borderRadius: "8px", textTransform: "none",
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui", fontSize: 12,
              bgcolor: "hsl(150 38% 17%)", color: "hsl(80 97% 72%)",
              boxShadow: "none",
              "&:hover": { bgcolor: "hsla(150, 38%, 17%, 0.9)", boxShadow: "none" },
            }}
          >
            Add entry
          </Button>

          <Box sx={{ display: "inline-flex", bgcolor: "hsl(140 8% 93%)", borderRadius: "8px", p: "3px" }}>
            <IconButton
              onClick={() => navWeek(-1)}
              aria-label="Previous week"
              sx={{ width: 28, height: 28, borderRadius: "6px", color: "hsl(150 30% 12%)", "&:hover": { bgcolor: "#fff" } }}
            >
              <ChevronLeft sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton
              onClick={() => navWeek(1)}
              aria-label="Next week"
              sx={{ width: 28, height: 28, borderRadius: "6px", color: "hsl(150 30% 12%)", "&:hover": { bgcolor: "#fff" } }}
            >
              <ChevronRight sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Box sx={{
        display: "grid",
        gridTemplateColumns: "56px repeat(7, minmax(0, 1fr))",
        borderTop: "1px solid hsl(140 8% 88%)",
        borderLeft: "1px solid hsl(140 8% 88%)",
        borderRadius: "10px",
        overflow: "hidden",
      }}>
        <Box sx={{
          borderRight: "1px solid hsl(140 8% 88%)",
          borderBottom: "1px solid hsl(140 8% 88%)",
          bgcolor: "hsl(140 10% 97%)",
        }} />
        {weekDays.map((d, i) => {
          const isToday = sameDay(d, new Date());
          return (
            <Box
              key={i}
              sx={{
                px: 1.5, py: 1, textAlign: "center",
                borderRight: "1px solid hsl(140 8% 88%)",
                borderBottom: "1px solid hsl(140 8% 88%)",
                bgcolor: isToday ? "hsla(150, 38%, 17%, 0.08)" : "hsl(140 10% 97%)",
              }}
            >
              <Typography sx={{ fontFamily: "Segoe UI Semibold, sans-serif, system-ui", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "hsl(150 10% 46%)" }}>
                {DAYS[i]}
              </Typography>
              <Typography sx={{ fontFamily: "Segoe UI Regular, sans-serif, system-ui", fontSize: 14, color: "hsl(150 30% 12%)" }}>
                {d.getDate()}
              </Typography>
            </Box>
          );
        })}

        {HOURS.map((h) => (
          <React.Fragment key={`row-${h}`}>
            <Box sx={{
              height: rowH,
              px: 1, py: 0.5,
              borderRight: "1px solid hsl(140 8% 88%)",
              borderBottom: "1px solid hsl(140 8% 88%)",
              fontFamily: "Segoe UI Regular, sans-serif, system-ui",
              fontSize: 10,
              color: "hsl(150 10% 46%)",
              display: "flex", alignItems: "flex-start", justifyContent: "flex-end",
            }}>
              {pad(h)}:00
            </Box>
            {weekDays.map((d, di) => (
              <Box
                key={`c-${h}-${di}`}
                component="button"
                type="button"
                onClick={() => openSlot(d, h)}
                aria-label={`Add entry on ${DAYS[di]} at ${pad(h)}:00`}
                sx={{
                  all: "unset",
                  cursor: "pointer",
                  boxSizing: "border-box",
                  height: rowH,
                  borderRight: "1px solid hsl(140 8% 88%)",
                  borderBottom: "1px solid hsl(140 8% 88%)",
                  position: "relative",
                  overflow: "visible",
                  transition: "background-color 0.15s ease",
                  "&:hover": { bgcolor: "hsla(150, 38%, 17%, 0.04)" },
                  "&:hover .add-icon": { opacity: 1 },
                }}
              >
                <Box className="add-icon" sx={{
                  position: "absolute",
                  top: 4, right: 4,
                  width: 16, height: 16, borderRadius: "50%",
                  bgcolor: "hsl(150 38% 17%)", color: "hsl(80 97% 72%)",
                  display: "grid", placeItems: "center",
                  opacity: 0,
                  transition: "opacity 0.15s ease",
                  pointerEvents: "none",
                }}>
                  <Add sx={{ fontSize: 10 }} />
                </Box>

                {entriesByDay[di].items
                  .filter((e) => new Date(e.startMs).getHours() === h)
                  .map((e) => {
                    const start = new Date(e.startMs);
                    const startMin = start.getMinutes();
                    const durMin = (e.endMs - e.startMs) / 60000;
                    const color = projectColors[e.project?.id] || e.project?.color;
                    const widthPct = 100 / e.laneCount;
                    return (
                      <Box
                        key={e.id}
                        component="span"
                        onClick={(ev) => { ev.stopPropagation(); openEntry(e); }}
                        title={`${e.project?.name || "—"} · ${formatHM(e.duration_secs || 0)}`}
                        sx={{
                          position: "absolute",
                          top: (startMin / 60) * rowH,
                          height: Math.max(20, (durMin / 60) * rowH - 2),
                          left: `calc(${e.lane * widthPct}% + 2px)`,
                          width: `calc(${widthPct}% - 4px)`,
                          borderRadius: "6px",
                          bgcolor: color,
                          color: "#fff",
                          px: 0.75, py: 0.5,
                          fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                          fontSize: 11,
                          overflow: "hidden",
                          boxShadow: "0 1px 2px hsla(150, 30%, 12%, 0.12)",
                          cursor: "pointer",
                          display: "block",
                          zIndex: 1,
                          transition: "filter 0.15s ease",
                          "&:hover": { filter: "brightness(1.08)", zIndex: 2 },
                        }}
                      >
                        <Box component="span" sx={{ display: "block", fontFamily: "Segoe UI Semibold, sans-serif, system-ui", fontSize: 11, lineHeight: 1.2, whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>
                          {e.project?.name || "—"}
                        </Box>
                        <Box component="span" sx={{ display: "block", fontFamily: "Segoe UI Regular, sans-serif, system-ui", fontSize: 10, color: "rgba(255,255,255,0.92)", whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>
                          {formatHM(e.duration_secs || 0)}
                        </Box>
                      </Box>
                    );
                  })}
              </Box>
            ))}
          </React.Fragment>
        ))}
      </Box>

      <CreateTimeEntryModal
        open={dialog.open}
        onClose={closeDialog}
        date={dialog.date}
        defaultHour={dialog.hour}
        entry={dialog.entry}
        projects={projects}
        authenticityToken={authenticity_token}
        onSaved={handleSaved}
      />
    </Paper>
  );
}
